import { useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import type { InlineKeyboardPanelProps } from '../../../types/inline-keyboard-panel.types';

/**
 * Clips a stable-height panel as the software keyboard reveals or covers it.
 * @param props - Full geometry, visible slot and controlled interaction state.
 * @returns A portaled dialog or inert retained surface during keyboard return.
 */
export const InlineKeyboardPanel = ({
  open,
  retained,
  containerRef,
  panelRef,
  height,
  occludedHeight,
  id,
  label,
  children,
}: InlineKeyboardPanelProps) => {
  useLayoutEffect(() => {
    if (panelRef.current) panelRef.current.inert = !open;
  }, [open, retained, panelRef]);
  if ((!open && !retained) || !containerRef.current) return null;
  return createPortal(
    <div
      ref={panelRef}
      id={id}
      role={open ? 'dialog' : undefined}
      aria-hidden={!open}
      aria-label={label}
      className="overflow-hidden bg-white"
      style={{ height: Math.max(0, height - occludedHeight) }}
    >
      <div className="border-t border-neutral-200" style={{ height }}>
        {children}
      </div>
    </div>,
    containerRef.current
  );
};
